import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faHandshake } from '@fortawesome/pro-solid-svg-icons';
import {
    IonAccordion,
    IonAccordionGroup,
    IonAvatar,
    IonCol,
    IonContent,
    IonItem,
    IonLabel,
    IonPage,
    IonRow,
    IonSearchbar,
    SearchbarChangeEventDetail,
} from '@ionic/react';
import {
    each,
    entries,
    filter,
    find,
    flow,
    groupBy,
    includes,
    keys,
    map,
    size,
    sumBy,
    toInteger,
    uniq,
} from 'lodash/fp';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { CircleIcon } from '../Icon';
import { useEmployees, useMads } from '../../hooks/useSwr';
import { OdooEmployee, OdooPointing } from '../../types/models';
import odoo from '../../utils/odoo';
import { PointingState } from '../../utils/odooUtils';
import { Header } from '../Header';
import { BottomSlotWarning } from '../../App';

interface IonSearchbarCustomEvent<T> extends CustomEvent<T> {
    detail: T;
    target: HTMLIonSearchbarElement;
}

type DisplayMatrixItemType = {
    title: string;
    icon: IconProp;
    hasSum?: boolean;
    showCount?: boolean;
};
export const displayMatrixPointage = {
    [PointingState.WaitValidation]: {
        title: 'Pointage à valider',
        icon: faHandshake,
        hasSum: true,
        showCount: true,
    },
    [PointingState.AdherentValidation]: {
        title: 'Pointage validé',
        icon: faHandshake,
        hasSum: true,
    },
    [PointingState.GeiqValidation]: {
        title: 'Pointage validé GEIQ',
        icon: faHandshake,
    },
    withoutPointingTime: {
        title: 'Pointage non saisi',
        icon: faHandshake,
    },
};

export const ValidationPointageMobile: React.FC = () => {
    const { data: employees, ids: employeesIds } = useEmployees();
    const { data: mads } = useMads();

    const [employeesByState, setEmployeesByState] = useState<{
        [key in PointingState | 'withoutPointingTime']?: (OdooEmployee & { count: number })[];
    }>({});

    const aggregateEmployeeData = (pointingByEmployee: { [employeeId: number]: OdooPointing[] }) =>
        map((employeeId: string) => ({
            ...find({ id: toInteger(employeeId) })(employees),
            count: pointingByEmployee[toInteger(employeeId)].length,
        }))(keys(pointingByEmployee));

    const sumOfEmployeesCount = (pointingState: PointingState | 'withoutPointingTime') =>
        sumBy('count')(employeesByState[pointingState]);

    const fetchData = async (ev?: IonSearchbarCustomEvent<SearchbarChangeEventDetail>) => {
        // defaultDataProcess is a function that takes an array of OdooPointing and returns an array of OdooEmployee with a count property that is the number of pointing times for this employee
        const defaultDataProcess = [groupBy('employee_id[0]'), aggregateEmployeeData];

        const pointingTimes: OdooPointing[] = await odoo.searchRead('hr.madper.app.pointing.time', {
            fields: ['id', 'employee_id', 'state'],
            domain: [
                ['origin_id', '=', false],
                ['employee_id', 'in', employeesIds],
                ...(ev && ev.target.value ? [['employee_id.name', 'ilike', ev.target.value]] : []),
            ],
        });

        let pointingTimesByState = groupBy('state')(pointingTimes);

        each((state: string) => {
            pointingTimesByState[state] = flow(defaultDataProcess)(pointingTimesByState[state]);
        }, keys(pointingTimesByState));

        const employeeWithPointing = flow([map('employee_id[0]'), uniq])(pointingTimes) as number[];
        const employeesWithoutPointingTime = flow([
            filter(
                (mad: (typeof mads)[0]) =>
                    mad.date_start !== null &&
                    mad.date_start < DateTime.now() &&
                    !includes(mad.employee_id[0], employeeWithPointing)
            ),
            ...defaultDataProcess,
        ])(mads);

        setEmployeesByState({
            ...pointingTimesByState,
            withoutPointingTime: employeesWithoutPointingTime,
        });
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <IonPage>
            <Header title="Pointage"></Header>
            <IonContent>
                <IonSearchbar
                    placeholder="Recherche employé"
                    className="my-2"
                    debounce={1000}
                    onIonChange={fetchData}
                />
                <div className="main-content-d pt-4">
                    <div className="content-validation-d left-offset-10">
                        <IonAccordionGroup multiple value={[PointingState.WaitValidation]}>
                            {map(
                                ([state, data]: [PointingState, DisplayMatrixItemType]) => (
                                    <IonAccordion value={state} className="mb-4" key={state}>
                                        <IonItem slot="header" className="ion-no-padding font-bold text-lg bg-white">
                                            <div className="ml-2">
                                                <CircleIcon icon={data.icon} size="text-3xl" />
                                            </div>
                                            <IonLabel className="ml-4">{data.title}</IonLabel>
                                            {data.hasSum && (
                                                <IonLabel slot="end" color="tertiary" className="text-right !flex-none">
                                                    {sumOfEmployeesCount(state)}
                                                </IonLabel>
                                            )}
                                        </IonItem>
                                        <div slot="content">
                                            {map(
                                                (employee) => (
                                                    <EmployeeItem
                                                        key={state + '_' + employee.id}
                                                        employee={employee}
                                                        state={state}
                                                        routePrefix="/validationdetail"
                                                        showCount={data.showCount}
                                                    />
                                                ),
                                                employeesByState[state]
                                            )}
                                            {size(employeesByState[state]) === 0 && (
                                                <IonItem className="ion-no-padding" lines="none">
                                                    <IonLabel className="text-center text-gray-500">
                                                        Aucun {data.title.toLowerCase()}
                                                    </IonLabel>
                                                </IonItem>
                                            )}
                                        </div>
                                    </IonAccordion>
                                ),
                                entries(displayMatrixPointage)
                            )}
                        </IonAccordionGroup>
                    </div>
                </div>
            </IonContent>
            <BottomSlotWarning />
        </IonPage>
    );
};

export const EmployeeItem: React.FC<{
    employee: OdooEmployee & { count: number };
    state: string;
    routePrefix: string;
    showCount?: boolean;
}> = ({ employee, state, routePrefix, showCount }) => {
    // FIXME: This is a hack to avoid a bug where adhérent has access to pointing data but not to the employee data (very weird), reproductible by logging in as an adhérent MZ4237C (demo-pointage-saisie.tetra-info.com)
    if (!employee.id) {
        return null;
    }

    return (
        <IonItem key={employee.id} href={`${routePrefix}/${employee.id}/${state}`}>
            <IonAvatar slot="start">
                <img
                    alt="Employee avatar"
                    className="h-full"
                    src={
                        employee.image_128
                            ? `data:image/jpeg;base64,${employee.image_128}`
                            : 'https://ionicframework.com/docs/img/demos/avatar.svg'
                    }
                />
            </IonAvatar>
            <IonCol>
                <IonRow>
                    <IonLabel>{employee.name}</IonLabel>
                </IonRow>
                <IonRow>
                    <IonLabel>{employee.best_phone}</IonLabel>
                </IonRow>
            </IonCol>
            {showCount && (
                <IonLabel slot="end" className="text-right !flex-none">
                    {employee.count}
                </IonLabel>
            )}
        </IonItem>
    );
};
